import { MainProcessBusEvent } from "../types/types";
import { mainBus } from "./MainBus";
import { showInFolder } from "./MainSystem";
import { EventEmitter } from "events";
import { getOrCreateMainLogger } from "../logger/main";
import path from "path";
import uuidv4 from "uuid-random";
import { unusedFilenameSync } from "unused-filename";
import fs from "fs";

const logger = getOrCreateMainLogger();

type DownloadItem = {
	id: string;
	item: Electron.DownloadItem;
	displayInfo: {
		filename: string;
		url: string;
		state: string;
		receivedBytes?: number;
		totalBytes: number;
		downloadPath: string;
	};
};

/**
 * Download Manager class for improved download handling.
 * Provides a wrapper around the electron downloadItem class to allow for user interaction.
 * A list of downloads from all windows is stored to provide information to the UI component.
 * Until Finsemble is restarted, downloads will remain on this list unless removed by the user.
 * This class is the single source of truth for everything except information set in user preferences.
 * There are several reasons for this:
 * 1. The UI component is not required to remain open and can fetch information on launch.
 * 2. Interaction with the download requires maintaining a reference to the event emitter received from the will-download event.
 *    This reference must remain on the main process side. We create an id to track a download between fea and the UI.
 * 3. The reference to the event emitter from the download will no longer function after restart.
 *    As such there is no reason to save download information between restarts with the exclusion of download settings.
 */
export class DownloadManager extends EventEmitter {
	downloadList: Array<DownloadItem> = [];

	saveLocation: string = "";

	constructor() {
		super();
		this.bind();
	}

	bind() {
		this.cancelDownload = this.cancelDownload.bind(this);
		this.pauseDownload = this.pauseDownload.bind(this);
		this.resumeDownload = this.resumeDownload.bind(this);
		this.removeDownloadItem = this.removeDownloadItem.bind(this);
		this.getDownloadList = this.getDownloadList.bind(this);
		this.setDownloadSavePath = this.setDownloadSavePath.bind(this);
		this.showDownloadedFileInFolder = this.showDownloadedFileInFolder.bind(this);
		this.clearDownloadList = this.clearDownloadList.bind(this);
		this.updatePostDownloadState = this.updatePostDownloadState.bind(this);
	}

	handleNewDownloadItem(item: Electron.DownloadItem) {
		const id = uuidv4();
		/*
		The save path can only be set as a response to a download event. The default is to ask for a location,
		set a save path if the user has specified one in preferences.
		If a file with the same name is already present at that location
		a number will be appended on the end eg: filename.txt (1)
		*/
		if (this.saveLocation) {
			item.setSavePath(unusedFilenameSync(path.join(this.saveLocation, item.getFilename())));
		}
		const filepath = item.getSavePath();
		const displayInfo = {
			url: item.getURL(),
			state: this.getDownloadState(item),
			filename: filepath ? path.basename(filepath) : item.getFilename(),
			receivedBytes: item.getReceivedBytes(),
			totalBytes: item.getTotalBytes(),
			downloadPath: filepath,
		};
		logger.debug(`DownloadManager: new download id: ${id} displayInfo: ${JSON.parse(JSON.stringify(displayInfo))}`);
		this.downloadList.push({ id, item, displayInfo });

		item.on("updated", (event, state) => {
			let downloadItem = this.downloadList.filter((i) => i.id === id)[0];
			if (!downloadItem) return;

			const updatedFilepath = item.getSavePath();
			// update display values that could have changed
			downloadItem.displayInfo.state = this.getDownloadState(downloadItem?.item);
			downloadItem.displayInfo.receivedBytes = downloadItem?.item?.getReceivedBytes();
			downloadItem.displayInfo.filename = updatedFilepath ? path.basename(updatedFilepath) : item.getFilename();
			downloadItem.displayInfo.totalBytes = downloadItem?.item?.getTotalBytes();
			downloadItem.displayInfo.downloadPath = updatedFilepath;

			// Only send the download up to be displayed if we already have a save location
			// This prevents the download from being shown before the user has submitted save options
			if (updatedFilepath) {
				mainBus.sendEvent("systemEvent.download-update", { id, displayInfo: downloadItem.displayInfo });
			}
		});

		item.once("done", (event, state) => {
			let downloadItem = this.downloadList.filter((i) => i.id === id)[0];
			if (!downloadItem) return;
			const updatedState = this.getDownloadState(downloadItem?.item);
			logger.debug(`DownloadManager: download ${updatedState} for ${id}`);
			downloadItem.displayInfo.state = updatedState;

			// Prevent downloads canceled at the save prompt from being displayed
			// If the user cancels from the save prompt, remove it from our list as we will never display it
			if (item.getSavePath()) {
				mainBus.sendEvent("systemEvent.download-update", { id, displayInfo: downloadItem.displayInfo });
			} else {
				const index = this.downloadList.findIndex(({ id }) => id === downloadItem.id);
				this.downloadList.splice(index, 1);
			}
		});
	}

	/**
	 * The electron getState function does not have "paused" as a possible value.
	 * For display (and typescript) purposes, this function returns the proper state
	 * @private
	 * @param item
	 * @returns "progressing" | "completed" | "cancelled" | "interrupted" | "paused" | "file-deleted"
	 */
	getDownloadState(item: Electron.DownloadItem) {
		const state = item?.getState();

		let pausedOrDeleted;
		if (state === "progressing" && item.isPaused()) {
			pausedOrDeleted = "paused";
		} else if (state === "completed" && !fs.existsSync(item?.getSavePath())) {
			pausedOrDeleted = "file-deleted";
		}
		return pausedOrDeleted ?? state;
	}

	/**
	 * Updates any state that can change after the download has completed.
	 * State changes during the update will be caught by electron events, but since these events
	 * stop sending after download completion we need to force the update for any state that we expect to change.
	 * Currently this is only tracking the state change from completed to file-deleted
	 * @param downloadItem
	 * @returns downloadItem
	 */
	updatePostDownloadState(downloadItem: DownloadItem) {
		if (downloadItem.displayInfo.state === "completed") {
			downloadItem.displayInfo.state = this.getDownloadState(downloadItem.item);
		}
		return downloadItem;
	}

	/**
	 * Responds with a full, updated list of download items
	 * This function is called after the download manager window is reopened.
	 * It will return an empty list if called after startup before any downloads have occurred.
	 * @param eventObj
	 */
	getDownloadList(eventObj: MainProcessBusEvent) {
		// Don't display any downloads that don't have a savePath - this will only happen when the user is prompted
		// for a location and either doesn't select a value or cancels from that dialog. We remove the download from the list
		// if the user cancels, but we also don't want to show it if the user hasn't made a selection.
		// We also remove the electron object that tracks the download and update post download state. There is no need to manually update
		// state during the download since electron will continue to send events until the download is completed.
		const prunedDownloadList = this.downloadList
			.filter((downloadItem) => !!downloadItem?.item?.getSavePath())
			.map((downloadItem) => this.updatePostDownloadState(downloadItem))
			.map(({ item, ...downloadItem }) => downloadItem);

		eventObj.respond(prunedDownloadList);
	}

	/**
	 * Show the downloaded file in it's folder
	 * @param eventObj { data:id }
	 */
	showDownloadedFileInFolder(eventObj: MainProcessBusEvent) {
		const id = eventObj.data;
		const downloadItem = this.downloadList.find((downloadItem) => downloadItem.id === id);
		const filepath = downloadItem?.item?.getSavePath();

		if (!downloadItem || !filepath) {
			logger.error(`DownloadManager: Unable to open folder for id: ${id} in folder at: ${filepath}`);
			eventObj.respond({ status: "error" });
			return;
		}
		if (!fs.existsSync(filepath)) {
			logger.info(`DownloadManager: ${filepath} has been removed from the disk`);
			this.updatePostDownloadState(downloadItem);
			// Trigger a state update if the file no longer exists on the harddrive
			mainBus.sendEvent("systemEvent.download-update", { id, displayInfo: downloadItem.displayInfo });
			eventObj.respond({ status: "error" });
		} else {
			logger.verbose(`DownloadManager: show download id: ${id} in folder at: ${filepath}`);
			showInFolder(filepath);
			eventObj.respond({ status: "success" });
		}
	}

	/**
	 * Removes a downloadItem from the list
	 * @param eventObj { data:id }
	 */
	removeDownloadItem(eventObj: MainProcessBusEvent) {
		const id = eventObj.data;
		logger.debug("DownloadManager: removing download entry for id", id);
		this.downloadList = this.downloadList?.filter((downloadItem) => downloadItem.id !== id);
		eventObj.respond({ status: "success" });
	}

	/**
	 * Cancels a download
	 * @param eventObj { data:id }
	 */
	cancelDownload(eventObj: MainProcessBusEvent) {
		const id = eventObj.data;
		logger.debug("DownloadManager: cancel download for id", id);
		let item = this.downloadList.find((downloadItem) => downloadItem.id === id)?.item;
		if (!item) {
			logger.error(`DownloadManager: Unable to cancel download, download matching ${id} not found`);
			eventObj.respond({ status: "error" });
			return;
		}
		try {
			item.cancel();
		} catch (err: any) {
			logger.error(err, err?.message);
		}
		eventObj.respond(item.getState() === "cancelled" ? { status: "success" } : { status: "error" });
	}

	/**
	 * Pauses a download
	 * @param eventObj { data:id }
	 */
	pauseDownload(eventObj: MainProcessBusEvent) {
		const id = eventObj.data;
		logger.debug("DownloadManager: pause download for id", id);
		const item = this.downloadList.find((downloadItem) => downloadItem.id === id)?.item;
		if (!item) {
			logger.error(`Error: pauseDownload downloadItem with an id matching: ${id} not found`);
			eventObj.respond({ status: "error" });
			return;
		}
		try {
			item.pause();
		} catch (err: any) {
			logger.error(err, err?.message);
		}
		eventObj.respond(item.isPaused() ? { status: "success" } : { status: "error" });
	}

	/**
	 * Resumes a paused download
	 * @param eventObj { data:id }
	 */
	async resumeDownload(eventObj: MainProcessBusEvent) {
		const id = eventObj.data;
		logger.debug("DownloadManager: resume download for id", id);
		const item = this.downloadList.find((downloadItem) => downloadItem.id === id)?.item;
		if (!item) {
			logger.error(`DownloadManager Error: resumeDownload downloadItem with an id matching: ${id} not found`);
			eventObj.respond({ status: "error" });
			return;
		}
		try {
			item.resume();
		} catch (err: any) {
			logger.error(err, err?.message);
		}
		eventObj.respond(!item.isPaused() ? { status: "success" } : { status: "error" });
	}

	/**
	 * Saves the download location set in user preferences
	 * This value must be set after the will-download event is received.
	 * By saving it after every change, we don't have to wait for a query
	 * when a download is initiated.
	 * @param eventObj saveLocation
	 */
	setDownloadSavePath(eventObj: MainProcessBusEvent) {
		this.saveLocation = eventObj.data;
		logger.debug("DownloadManager: setting updated save path", this.saveLocation);
	}

	/**
	 * This is just for testing so the list can be wiped without clearing cache.
	 * The component will only reflect this change if it's closed and reopened
	 * because the only time we send the entire list is when the component starts
	 * @param eventObj
	 */
	clearDownloadList(eventObj: MainProcessBusEvent) {
		this.downloadList = [];
	}
}
